import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";

import type mapboxgl from "mapbox-gl";
import type { GeoJSONSource } from "mapbox-gl";

import {
  SELECTED_WELL_LAYER,
  SELECTED_WELL_POINT_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT,
  WELL_SOURCE_LAYER
} from "../../../constants/mapLayers.constants";
import { EntitiesInGroupModel } from "../contexts/MapContextState";
import { waitForStyleToLoad } from "../utils";
import { emptyFeatureCollection } from "../utils/emptyFeatureCollection";

const STYLE_LOAD_RETRY_DELAY_MS = 100;
const MIN_ZOOM_FOR_HIGHLIGHTING = 6; // Don't highlight at zoom levels below this to prevent performance issues
export const MAX_ENTITIES_TO_HIGHLIGHT = 50000; // Maximum entities to highlight to prevent memory issues
const DEBOUNCE_DELAY_MS = 150; // Delay to let map state stabilize after zoom/pan
const MOVE_END_DEBOUNCE_MS = 200; // Delay after map movement to re-apply highlights

/**
 * Type for highlight ID mapping (UWI -> color)
 */
type HighlightIds = StringMap<string>;

/**
 * Strip well/facility count prefix like "(16) " from group names
 */
function stripWellCount(str: string): string {
  return str.replace(/^\(\d+\)\s*/, "");
}

/**
 * Build highlight IDs map from selected groups
 */
export function buildHighlightIds(
  selectedGroups: Maybe<string[]>,
  entitiesInGroup: Maybe<{ [title: string]: EntitiesInGroupModel }>
): HighlightIds {
  const highlightIds: HighlightIds = {};

  for (const group of selectedGroups ?? []) {
    // Try exact match first
    let entityGroup = entitiesInGroup?.[group];

    // If no exact match, try stripping well counts (for screenshot mode where counts differ)
    if (!entityGroup && entitiesInGroup) {
      const groupName = stripWellCount(group);
      const matchingKey = Object.keys(entitiesInGroup).find(
        (key) => stripWellCount(key) === groupName
      );
      if (matchingKey) {
        entityGroup = entitiesInGroup[matchingKey];
      }
    }

    if (!entityGroup) {
      continue;
    }

    const uwis = entityGroup.uwis;
    Object.keys(uwis).forEach((uwi) => {
      highlightIds[uwi] = uwis[uwi];
    });
  }

  return highlightIds;
}

/**
 * Assign color property to features based on highlight IDs
 */
function assignFeatureColors(
  features: mapboxgl.MapboxGeoJSONFeature[],
  highlightIds: HighlightIds
) {
  features.forEach((feat) => {
    feat.properties = {
      ...feat.properties,
      color: highlightIds[feat.properties.Uwi]
    };
  });
}

/**
 * Ensure point source exists on the map
 */
function ensurePointSourceExists(mapbox: mapboxgl.Map): void {
  if (!mapbox.getSource(SELECTED_WELL_POINT_LAYER)) {
    mapbox.addSource(SELECTED_WELL_POINT_LAYER, {
      type: "geojson",
      data: { ...emptyFeatureCollection }
    });
  }
}

/**
 * Query map features and apply highlight colors to the selected well/point sources.
 * Returns true if highlights were successfully applied, false otherwise.
 */
async function applyHighlights(
  mapbox: mapboxgl.Map,
  selectedGroups: Maybe<string[]>,
  entitiesInGroup: Maybe<{ [title: string]: EntitiesInGroupModel }>,
  hasHighlightDataRef: React.MutableRefObject<boolean>
): Promise<boolean> {
  // Wait for style to load
  const isStyleLoaded = await waitForStyleToLoad(mapbox, STYLE_LOAD_RETRY_DELAY_MS);
  if (!isStyleLoaded) {
    return false;
  }

  // Safety check: Don't query features while tiles are loading to prevent race condition crashes
  if (!mapbox.areTilesLoaded()) {
    return false;
  }

  // Check zoom level - don't highlight at low zoom to prevent performance issues
  const currentZoom = mapbox.getZoom();

  if (currentZoom < MIN_ZOOM_FOR_HIGHLIGHTING) {
    // Clear any existing highlights when zoom is too low
    if (hasHighlightDataRef.current) {
      const wellSource = mapbox.getSource(SELECTED_WELL_LAYER) as GeoJSONSource;
      const pointSource = mapbox.getSource(SELECTED_WELL_POINT_LAYER) as GeoJSONSource;
      wellSource?.setData(emptyFeatureCollection);
      pointSource?.setData(emptyFeatureCollection);
      hasHighlightDataRef.current = false;
    }
    return false;
  }

  // Get sources and ensure they exist
  const selectedWellSource = mapbox.getSource(SELECTED_WELL_LAYER) as GeoJSONSource;
  ensurePointSourceExists(mapbox);
  const selectedPointSource = mapbox.getSource(
    SELECTED_WELL_POINT_LAYER
  ) as GeoJSONSource;

  if (!selectedWellSource || !selectedPointSource || !entitiesInGroup) {
    return false;
  }

  // Build highlight IDs from selected groups
  const highlightIds = buildHighlightIds(selectedGroups, entitiesInGroup);
  const entityCount = Object.keys(highlightIds).length;

  // If no entities to highlight, clear existing highlights and return
  if (entityCount === 0) {
    if (hasHighlightDataRef.current) {
      selectedWellSource.setData(emptyFeatureCollection);
      selectedPointSource.setData(emptyFeatureCollection);
      hasHighlightDataRef.current = false;
    }
    return false;
  }

  // Safety check: bail out if too many entities to prevent memory issues
  if (entityCount > MAX_ENTITIES_TO_HIGHLIGHT) {
    Sentry.logger.warn(
      `Entity count ${entityCount} exceeds maximum ${MAX_ENTITIES_TO_HIGHLIGHT}, skipping highlight to prevent memory issues`
    );
    if (hasHighlightDataRef.current) {
      selectedWellSource.setData(emptyFeatureCollection);
      selectedPointSource.setData(emptyFeatureCollection);
      hasHighlightDataRef.current = false;
    }
    return false;
  }

  // Double-check tiles are loaded right before querying - map state may have changed
  if (!mapbox.areTilesLoaded()) {
    return false;
  }

  // Query features from map sources (wrapped in try-catch to handle race condition crashes)
  try {
    const features = mapbox.querySourceFeatures(WELL_LAYER, {
      sourceLayer: WELL_SOURCE_LAYER,
      filter: ["has", ["get", "Uwi"], ["literal", highlightIds]]
    });

    const pointFeatures = mapbox.querySourceFeatures(WELL_LAYER_POINT, {
      sourceLayer: WELL_SOURCE_LAYER,
      filter: [
        "all",
        ["has", ["get", "Uwi"], ["literal", highlightIds]],
        ["!=", ["get", "group"], "leg"]
      ]
    });

    // Assign colors to features
    assignFeatureColors(features, highlightIds);
    assignFeatureColors(pointFeatures, highlightIds);

    // Update map sources with highlighted features
    const lineData = { ...emptyFeatureCollection, features };
    const pointData = { ...emptyFeatureCollection, features: pointFeatures };

    selectedWellSource.setData(lineData);
    selectedPointSource.setData(pointData);
    hasHighlightDataRef.current = true;
    return true;
  } catch (error) {
    // Highlighting failed during map state transition - log and bail out gracefully
    Sentry.logger.warn("Highlighting failed during map state transition", {
      error
    });
    return false;
  }
}

/**
 * Highlight selected wells and facilities on the map based on legend selection.
 * This hook creates overlay layers (SELECTED_WELL_LAYER, SELECTED_WELL_POINT_LAYER) that
 * highlight wells when users select items from the legend.
 *
 * The hook also listens for `moveend` events to re-apply highlights after map movement,
 * which handles cases where the source data is externally cleared (e.g., when entering
 * screenshot mode triggers a map container resize).
 *
 * Performance safeguards:
 * - Minimum zoom level (>= 6) required for highlighting to prevent querying/processing
 *   thousands of features at low zoom levels.
 * - Maximum entity limit (50,000) to prevent memory exhaustion from processing massive datasets.
 * - Zoom threshold crossing detection: When zooming from <6 to >=6, enters a 500ms cooldown
 *   period where all highlighting is skipped (prevents crash during heavy tile loading).
 * - 150ms debounce to let map state stabilize after rapid interactions.
 * - Try-catch wrapper around all queries and processing to catch any remaining race conditions.
 *
 * @param mapboxRef - Reference to the Mapbox instance
 * @param selectedGroups - Array of selected group titles from the legend
 * @param entitiesInGroup - Mapping of group titles to their entities (wells/facilities)
 */
export function useHighlightSelection(
  mapboxRef: React.RefObject<mapboxgl.Map>,
  selectedGroups: string[] | undefined,
  entitiesInGroup: { [title: string]: EntitiesInGroupModel } | undefined
) {
  // Track if we've set highlight data to avoid unnecessary cleanup
  const hasHighlightDataRef = useRef(false);
  // Track previous zoom level to detect threshold crossings
  const previousZoomRef = useRef<number | null>(null);
  // Track when we last crossed the threshold to enforce a cooldown period
  const thresholdCrossedAtRef = useRef<number | null>(null);
  // Refs to keep latest values accessible to the moveend event handler
  const selectedGroupsRef = useRef(selectedGroups);
  const entitiesInGroupRef = useRef(entitiesInGroup);
  selectedGroupsRef.current = selectedGroups;
  entitiesInGroupRef.current = entitiesInGroup;

  // Read the map instance during render so it can be used as a dependency.
  // mapboxRef is a stable ref, but mapboxRef.current is null until the map is
  // created in a later effect. Including the instance in deps ensures the
  // moveend listener is registered once the map becomes available.
  const mapbox = mapboxRef.current;

  // Stable callback for re-applying highlights (used by moveend handler)
  const reapplyHighlights = useCallback(async () => {
    const mapbox = mapboxRef.current;
    if (!mapbox) return;

    const groups = selectedGroupsRef.current;
    const entities = entitiesInGroupRef.current;
    if (!groups?.length || !entities) return;

    try {
      await applyHighlights(mapbox, groups, entities, hasHighlightDataRef);
    } catch (error) {
      Sentry.captureException(error);
      Sentry.logger.error("Failed to re-apply highlight selection after map move", {
        error
      });
    }
  }, [mapboxRef]);

  // Listen for moveend events to re-apply highlights after map movement.
  // This handles the case where source data is externally cleared (e.g., clearSelectedData
  // on movestart during screenshot mode transitions that trigger map container resize).
  useEffect(() => {
    if (!mapbox) return;

    let moveEndDebounce: ReturnType<typeof setTimeout> | null = null;

    const handleMoveEnd = () => {
      // Only re-apply if there should be active highlights
      if (!selectedGroupsRef.current?.length) return;

      if (moveEndDebounce) clearTimeout(moveEndDebounce);
      moveEndDebounce = setTimeout(() => {
        reapplyHighlights();
      }, MOVE_END_DEBOUNCE_MS);
    };

    mapbox.on("moveend", handleMoveEnd);

    return () => {
      if (moveEndDebounce) clearTimeout(moveEndDebounce);
      mapbox.off("moveend", handleMoveEnd);
    };
  }, [mapbox, reapplyHighlights]);

  useEffect(() => {
    if (!mapboxRef.current) {
      return;
    }

    const mapbox = mapboxRef.current;
    const currentZoom = mapbox.getZoom();
    const prevZoom = previousZoomRef.current;
    const now = Date.now();

    // Check if we just crossed the min zoom threshold (likely during heavy tile loading)
    const crossedThreshold =
      prevZoom !== null &&
      prevZoom < MIN_ZOOM_FOR_HIGHLIGHTING &&
      currentZoom >= MIN_ZOOM_FOR_HIGHLIGHTING;

    // Update previous zoom for next render
    previousZoomRef.current = currentZoom;

    // If we just crossed the threshold, record the timestamp and skip
    if (crossedThreshold) {
      thresholdCrossedAtRef.current = now;
      return;
    }

    // If we recently crossed the threshold, skip for 500ms cooldown period
    // (effect may fire multiple times after crossing due to selection changes, etc.)
    if (thresholdCrossedAtRef.current !== null) {
      const timeSinceCrossing = now - thresholdCrossedAtRef.current;
      if (timeSinceCrossing < 500) {
        return;
      }
      // Cooldown period over, clear the timestamp
      thresholdCrossedAtRef.current = null;
    }

    // Debounce to let map state stabilize after rapid zoom/pan changes
    const debounceTimeout = setTimeout(async () => {
      try {
        await applyHighlights(
          mapbox,
          selectedGroups,
          entitiesInGroup,
          hasHighlightDataRef
        );
      } catch (error) {
        Sentry.captureException(error);
        Sentry.logger.error("Failed to highlight selection", { error });
      }
    }, DEBOUNCE_DELAY_MS);

    // Cleanup: cancel the pending debounce. Don't clear source data here —
    // the next effect run will either clear (entityCount === 0) or re-populate.
    // Clearing here would cause a visible flicker when dependencies get new
    // references without the selection actually changing (e.g., screenshot transitions).
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [mapboxRef, selectedGroups, entitiesInGroup]);
}
